import { isLeft, isRight } from 'fp-ts/Either';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { TRPC } from '../../../contexts/TRPC';
import { captureAndReportError } from '../../../utils/errorReporting';
import type { HubspotTicketDetailData } from '../types';
import { normalizeTicketDetail } from '../utils';

interface UseHubspotTicketDetailParams {
  ticketId: string;
  trpc: TRPC | null;
}

interface UseHubspotTicketDetailResult {
  data: HubspotTicketDetailData | null;
  isLoading: boolean;
  error: Error | null;
  setData: Dispatch<SetStateAction<HubspotTicketDetailData | null>>;
  refetch: () => Promise<void>;
}

export function useHubspotTicketDetail({
  ticketId,
  trpc,
}: UseHubspotTicketDetailParams): UseHubspotTicketDetailResult {
  const [data, setData] = useState<HubspotTicketDetailData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const requestIdRef = useRef(0);

  const fetchTicketDetail = useCallback(
    async ({ resetData = false }: { resetData?: boolean } = {}) => {
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;

      const isCurrentRequest = () => requestIdRef.current === requestId;

      if (resetData) {
        setData(null);
      }

      setIsLoading(true);
      setError(null);

      if (!trpc || !trpc.client) {
        if (isCurrentRequest()) {
          setError(new Error('TRPC context not initialized'));
          setIsLoading(false);
        }
        return;
      }

      try {
        const result = await trpc.client.getHubspotTicketById.query({
          ticketId,
        });

        if (!isCurrentRequest()) {
          return;
        }

        if (isRight(result)) {
          setData(normalizeTicketDetail(result.right));
        } else if (isLeft(result)) {
          setError(
            new Error(result.left.message || 'Failed to fetch ticket details'),
          );
        }
      } catch (err) {
        if (!isCurrentRequest()) {
          return;
        }

        captureAndReportError('Error fetching ticket detail:', err);
        setError(err as Error);
      } finally {
        if (isCurrentRequest()) {
          setIsLoading(false);
        }
      }
    },
    [ticketId, trpc],
  );

  useEffect(() => {
    void fetchTicketDetail({ resetData: true });

    return () => {
      requestIdRef.current += 1;
    };
  }, [fetchTicketDetail]);

  const refetch = useCallback(() => fetchTicketDetail(), [fetchTicketDetail]);

  return {
    data,
    isLoading,
    error,
    setData,
    refetch,
  };
}
